import React, { useEffect, useState } from "react";
import { FiLoader, FiTrash } from "react-icons/fi";
import methodModel from "../../methods/methods";

// Large uploads can return before the compressed object is ready.
const LargeVideoPreview = ({ src, onRemove, videoRef }) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const isAbsoluteUrl = /^https?:\/\//i.test(src);
  const originalUrl = isAbsoluteUrl ? src : methodModel.noImg(src);
  const compressedUrl = isAbsoluteUrl
    ? src
    : methodModel.noImg(`compressed/${src.replace(/^compressed\//, "")}`);
  const fallbackUrl = originalUrl.replace("/compressed/", "/");
  const sources = [...new Set([compressedUrl, originalUrl, fallbackUrl])];

  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => {
      setSourceIndex(0);
      setRetryCount((count) => count + 1);
      setWaiting(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [waiting]);

  const handleError = () => {
    setReady(false);
    if (sourceIndex < sources.length - 1) {
      setSourceIndex((index) => index + 1);
    } else if (retryCount < 60) {
      setWaiting(true);
    } else {
      setFailed(true);
    }
  };

  return (
    <div
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      style={{ width: 300, maxWidth: "100%" }}
    >
      <div className="relative bg-black">
        <video
          key={`${sourceIndex}-${retryCount}`}
          ref={videoRef}
          src={sources[sourceIndex]}
          width={300}
          className="block h-44 w-full object-contain"
          controls
          playsInline
          preload="metadata"
          onError={handleError}
          onLoadedMetadata={() => {
            setReady(true);
            setWaiting(false);
            setFailed(false);
          }}
        />
        <button
          type="button"
          aria-label="Remove video"
          title="Remove video"
          className="absolute top-2 right-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#540C0F] shadow-sm hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#540C0F] focus-visible:ring-offset-2"
          onClick={onRemove}
        >
          <FiTrash className="h-4 w-4 shrink-0" aria-hidden="true" />
        </button>
      </div>
      {!failed && !ready && (waiting || retryCount > 0) && (
        <div role="status" className="flex items-start gap-2 border-t border-gray-200 bg-gray-50 px-3 py-3">
          <FiLoader className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#540C0F]" aria-hidden="true" />
          <div className="min-w-0 text-sm">
            <p className="font-medium text-gray-700">Waiting for video</p>
            <p className="mt-1 text-xs text-gray-500">
              Video is not ready to play yet. Large videos may take a few minutes.
            </p>
          </div>
        </div>
      )}
      {failed && (
        <div role="alert" className="border-t border-red-100 bg-red-50 px-3 py-3 text-sm text-red-700">
          Video could not be played. It may still be processing or use an unsupported format.
          <button
            type="button"
            className="underline ml-2"
            onClick={() => {
              setSourceIndex(0);
              setRetryCount(0);
              setFailed(false);
              setWaiting(false);
              setReady(false);
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default LargeVideoPreview;
